import { Component, OnInit, Inject, OnDestroy, Injector, NgZone, AfterViewInit } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, RedirectRequest, EventMessage, EventType, IdTokenClaims, AccountInfo, ServerError } from '@azure/msal-browser';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { IEnvironmentVariables } from 'src/environments/IEnvironment';
import { AuthenticationService, BroadcastAppService, CONFIG_ENV, PostLoginRedirectService, AuthorizationHighlightStateService } from '@core/service';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { SharedServiceComponent } from '@shared/component/shared-service/shared-service.component';
import { FeatureToggleService, SecurityService } from '@mynexus/mynexus-ui-lib';
import { ExternalAccessibilityObserverService } from '@shared/services/external-accessibility-observer';
import { FeatureFlags } from '@core/enumeration';
import { InternalAccessibilityObserverService } from '@shared/services/internal-accessibility-observer';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy  {
  isIframe = false;
  permissionsInitialized = false;

  private readonly _destroying$ = new Subject<void>();
  private listenerWasAdded = false;
  accessibilityObserverEnabled$: Observable<boolean> = of(false);

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    @Inject(CONFIG_ENV) private configEnv: IEnvironmentVariables,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private injector: Injector,
    private postLoginRedirectService: PostLoginRedirectService,
    private authenticationService: AuthenticationService,
    private securityService: SecurityService,
    private broadcastAppService: BroadcastAppService,
    private accessibilityObserver: ExternalAccessibilityObserverService,
    private accessibilityObserverInternal: InternalAccessibilityObserverService,
    private featureToggleService: FeatureToggleService,
    private ngZone: NgZone,
    private authHighlightStateService: AuthorizationHighlightStateService
  ) {
    customElements.define('mnc-shared-services', createCustomElement(SharedServiceComponent, { injector: this.injector }));
    (window as any).shellBroadcastAppService = this.broadcastAppService;

    // Notes specific use case for NotesSidebarComponent in Note.MFE. Had a race condition where security module
    // was being accessed before the MFE was finished bootstrapping and permissions were loaded. We want to
    // let the MFE finish loading before subscribing to any custom events.
    document.addEventListener('mnc-notes-loaded', () => {
      this.broadcastAppService.initializeBroadcastService();
      this.listenerWasAdded = true;
    });

    this.postLoginRedirectService.intercept();
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    // In MSAL Browser v5, navigateToLoginRequestUrl has been removed from BrowserAuthOptions.
    // Pass it here to prevent MSAL from navigating to the original URL after redirect
    // (Angular's own routing handles post-login navigation via postLoginRedirect()).
    this.authService.handleRedirectObservable({ navigateToLoginRequestUrl: false }).subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result?.account) {
          this.authService.instance.setActiveAccount(result.account);
        }
      },
      error: (err) => {
        console.error('[MSAL] Redirect handling error:', err);
        // B2C returns a generic server_error when the user cancels the flow or
        // when there is stale interaction state in session storage.
        // Clear the state and re-initiate login so the user is not stuck.
        if (err instanceof ServerError && err.errorCode === 'server_error') {
          this.authService.instance.clearCache();
          this.login();
        }
      },
    });

    // In MSAL Browser v5, account storage events are always enabled — enableAccountStorageEvents() has been removed.
    // ACCOUNT_ADDED/ACCOUNT_REMOVED are replaced by LOGIN_SUCCESS/LOGOUT_SUCCESS in v5.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.LOGOUT_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGOUT_SUCCESS && this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        // In v5: LOGIN_SUCCESS payload is AccountInfo (not AuthenticationResult); SSO_SILENT_SUCCESS is merged into ACQUIRE_TOKEN_SUCCESS.
        // Listen only to ACQUIRE_TOKEN_SUCCESS which covers all successful token acquisitions including login.
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (
          idtoken.acr === this.configEnv.b2cPolicies.names.signUpSignIn ||
          idtoken.tfp === this.configEnv.b2cPolicies.names.signUpSignIn ||
          idtoken.tfp === this.configEnv.b2cPolicies.names.signUpSignInRegence||
          idtoken.tfp === this.configEnv.b2cPolicies.names.signUpSignInportalextsso
        ) {
          this.authService.instance.setActiveAccount(payload.account);

          if (!this.permissionsInitialized) {
            this.initializePermissions();
          }
        }

        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         */
        if (
          idtoken.acr === this.configEnv.b2cPolicies.names.resetPassword ||
          idtoken.tfp === this.configEnv.b2cPolicies.names.resetPassword
        ) {
          this.logout();
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        // In v5: LOGIN_FAILURE is replaced by ACQUIRE_TOKEN_FAILURE.
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {

        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          const resetPasswordFlowRequest: RedirectRequest = {
            authority: `${this.configEnv.b2cPolicies.authorityBaseUrl}/${this.configEnv.b2cPolicies.names.resetPassword}`,
            scopes: this.configEnv.authConfig.angularConfig.consentScopes,
          };

          this.login(resetPasswordFlowRequest);
        }
      });

    // Listens for all navigation events and extracts auth number when navigating to auth-dashboard
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this._destroying$)
    ).subscribe((event: NavigationEnd) => {
      const dmeposMatch = event.urlAfterRedirects.match(/dmepos\/auth-dashboard\/([^/?]+)/);
      if (dmeposMatch) {
        this.authHighlightStateService.addCurrentAndVisitedAuth('dmepos', dmeposMatch[1]);
      }

      const pacMatch = event.urlAfterRedirects.match(/(?:pac-im|pac-commercial)\/auth-dashboard\/([^/?]+)/);
      if (pacMatch) {
        this.authHighlightStateService.addCurrentAndVisitedAuth('pac', pacMatch[1]);
      }

      const hhMatch = event.urlAfterRedirects.match(/home-health\/auth-dashboard\/([^/?]+)/);
      if (hhMatch) {
        this.authHighlightStateService.addCurrentAndVisitedAuth('hh', hhMatch[1]);
      }

      const admissionMatch = event.urlAfterRedirects.match(/auth-dashboard\/([^/?]+)/);
      if (admissionMatch) {
        this.authHighlightStateService.addCurrentAndVisitedAuth('admission', admissionMatch[1]);
      }
    });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }

    if (this.authService.instance.getActiveAccount()) {
      this.accessibilityObserverEnabled$ = this.featureToggleService.getFeatureAccess(FeatureFlags.ExternalWCAGAccessibilityObserver);
      this.postLoginRedirect();
    }
  }

  ngAfterViewInit(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        combineLatest([
            this.ngZone.onStable.pipe(take(1)),
            this.accessibilityObserverEnabled$.pipe(filter(Boolean), take(1))
          ])
          .subscribe(() => {
            const observer = this.authenticationService.isExternalUser ?
              this.accessibilityObserver : this.accessibilityObserverInternal;
            this.ngZone.runOutsideAngular(() => observer.start());
          })
      });
  }

  login(userFlowRequest?: RedirectRequest) {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect(userFlowRequest);
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    if (this.listenerWasAdded) {
      document.removeAllListeners('mnc-notes-loaded');
    }

    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.accessibilityObserver.stop();
  }

  private postLoginRedirect() {
    let path = '/home';
    const navigationExtras: NavigationExtras = { replaceUrl: true };

    const redirectPath = this.postLoginRedirectService.getRedirectPath();

    if (redirectPath) {
      path = redirectPath.url;

      if (redirectPath.query) {
        navigationExtras.queryParams = redirectPath.query;
      }
    }

    this.router.navigate([path], navigationExtras);
  }

  private initializePermissions(): void {
    this.authenticationService.getUserPermissions()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (permissions) => {
          this.securityService.setUserPermissions(Object.values(permissions).flat())
          this.securityService.setAllowEmptyPermission(false);
          this.permissionsInitialized = true;
        },
        error: (err) => console.error('Failed to load permissions:', err),
      });
  }
}
